import { makeAutoObservable, observable, runInAction } from "mobx";
import { track, type AnalyticsSource } from "@/lib/analytics";
import { changedFields } from "@/lib/changed-fields";
import type { RootStore } from "./root.store";
import type {
  ICalendarSource,
  IGoogleDomainWideConfig,
  IHolidayCalendar,
  IHolidayCalendarDate,
  IHolidayCalendarDateImportResult,
  IHolidayCalendarDatePatch,
  IHolidayCalendarDateQuery,
  IHolidayCalendarDateWrite,
  IHolidayCalendarPerson,
  IHolidayCalendarSuggestion,
  IHolidayCalendarSyncResult,
  IHolidayCalendarWrite,
  IHolidayCountry,
  IOutlookDomainWideConfig,
} from "@/types/workspace";

/** One page of a calendar's dates, in the store's own camelCase shape.
 *  `nextCursor` is null on the last page, which is what makes it safe to use as
 *  the "there is more to fetch" guard for the table's infinite scroll. */
export type THolidayCalendarDatesPage = {
  results: IHolidayCalendarDate[];
  totalCount: number;
  nextCursor: string | null;
  hasNext: boolean;
};

export class WorkspaceCalendarsStore {
  userSources: ICalendarSource[] = [];
  holidayCountries: IHolidayCountry[] = [];
  holidayCalendars: IHolidayCalendar[] = [];
  holidayCalendarSuggestions: IHolidayCalendarSuggestion[] = [];
  isLoadingHolidayCalendars = false;
  isLoadingHolidayCalendarSuggestions = false;
  activeHolidayCalendarId: string | null = null;
  myHolidayCalendar: ICalendarSource | null = null;
  googleConfig: IGoogleDomainWideConfig | null = null;
  outlookConfig: IOutlookDomainWideConfig | null = null;
  isLoadingUserSources = false;
  isLoadingHolidayCountries = false;
  isLoadingMyHolidayCalendar = false;
  isSavingMyHolidayCalendar = false;
  isLoadingGoogleConfig = false;
  isLoadingGoogleMembers = false;
  isLoadingOutlookConfig = false;
  isLoadingOutlookMembers = false;
  activeSourceId: string | null = null;
  activeGoogleAction = "";
  activeOutlookAction = "";

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(
      this,
      {
        rootStore: false,
        userSources: observable.ref,
        holidayCountries: observable.ref,
        holidayCalendars: observable.ref,
        holidayCalendarSuggestions: observable.ref,
        myHolidayCalendar: observable.ref,
        googleConfig: observable.ref,
      } as never,
      { autoBind: true },
    );
  }

  async fetchUserSources(): Promise<ICalendarSource[]> {
    this.isLoadingUserSources = true;
    try {
      const sources = await this.rootStore.workspaceCalendarService.listUserSources();
      runInAction(() => {
        this.userSources = sources;
      });
      return sources;
    } finally {
      runInAction(() => {
        this.isLoadingUserSources = false;
      });
    }
  }

  async uploadUserSource(
    file: File,
    name: string,
    workspaceSlug?: string,
    source: AnalyticsSource = "page",
  ): Promise<ICalendarSource> {
    const created = await this.rootStore.workspaceCalendarService.uploadUserICS(
      file,
      name,
      workspaceSlug,
    );
    // Before the refetch, so a failed reload doesn't drop the event for an
    // upload that went through (NG-2061).
    track("calendar_connected", { provider: "ics", source_id: created.id, source });
    await this.fetchUserSources();
    return created;
  }

  async deleteUserSource(sourceId: string, source: AnalyticsSource = "page"): Promise<void> {
    this.activeSourceId = sourceId;
    try {
      await this.rootStore.workspaceCalendarService.deleteUserSource(sourceId);
      track("calendar_deleted", { source_id: sourceId, source });
      runInAction(() => {
        this.userSources = this.userSources.filter((source) => source.id !== sourceId);
      });
    } finally {
      runInAction(() => {
        this.activeSourceId = null;
      });
    }
  }

  async fetchHolidayCountries(): Promise<IHolidayCountry[]> {
    if (this.holidayCountries.length > 0) return this.holidayCountries;

    this.isLoadingHolidayCountries = true;
    try {
      const result = await this.rootStore.workspaceCalendarService.listHolidayRegions();
      const countries = result?.countries ?? [];
      runInAction(() => {
        this.holidayCountries = countries;
      });
      return countries;
    } catch (error) {
      // The country/region catalogue is optional picker data — degrade to an
      // empty list rather than rejecting (the region picker just renders no
      // options). Log so a real outage is still visible.
      console.warn("Failed to load holiday country/region catalogue", error);
      return [];
    } finally {
      runInAction(() => {
        this.isLoadingHolidayCountries = false;
      });
    }
  }

  async fetchMyHolidayCalendar(slug: string): Promise<ICalendarSource | null> {
    this.isLoadingMyHolidayCalendar = true;
    try {
      const source = await this.rootStore.workspaceCalendarService.getMyHolidayCalendar(slug);
      runInAction(() => {
        this.myHolidayCalendar = source;
      });
      return source;
    } finally {
      runInAction(() => {
        this.isLoadingMyHolidayCalendar = false;
      });
    }
  }

  async setMyHolidayCalendar(
    slug: string,
    data: { country: string; subdivision?: string },
    source: AnalyticsSource = "page",
  ): Promise<ICalendarSource> {
    this.isSavingMyHolidayCalendar = true;
    try {
      const calendar = await this.rootStore.workspaceCalendarService.setMyHolidayCalendar(
        slug,
        data,
      );
      runInAction(() => {
        this.myHolidayCalendar = calendar;
      });
      // A country code, not a person's detail. Before the refetch, as above.
      track("holiday_calendar_selected", {
        country: data.country,
        has_subdivision: Boolean(data.subdivision),
        source,
      });
      await this.fetchUserSources();
      return calendar;
    } finally {
      runInAction(() => {
        this.isSavingMyHolidayCalendar = false;
      });
    }
  }

  async deleteMyHolidayCalendar(slug: string, source: AnalyticsSource = "page"): Promise<void> {
    this.isSavingMyHolidayCalendar = true;
    try {
      await this.rootStore.workspaceCalendarService.deleteMyHolidayCalendar(slug);
      track("holiday_calendar_cleared", { source });
      runInAction(() => {
        this.myHolidayCalendar = null;
        this.userSources = this.userSources.filter(
          (source) => source.source_type !== "public_holidays",
        );
      });
    } finally {
      runInAction(() => {
        this.isSavingMyHolidayCalendar = false;
      });
    }
  }

  // --- Admin-managed holiday calendars (M4–M6) ---

  async fetchHolidayCalendars(slug: string): Promise<IHolidayCalendar[]> {
    this.isLoadingHolidayCalendars = true;
    try {
      const calendars =
        (await this.rootStore.workspaceCalendarService.listHolidayCalendars(slug)) ?? [];
      runInAction(() => {
        this.holidayCalendars = calendars;
      });
      return calendars;
    } finally {
      runInAction(() => {
        this.isLoadingHolidayCalendars = false;
      });
    }
  }

  async createHolidayCalendar(
    slug: string,
    data: IHolidayCalendarWrite,
    source: AnalyticsSource = "page",
  ): Promise<IHolidayCalendar> {
    this.activeHolidayCalendarId = "creating";
    try {
      const calendar = await this.rootStore.workspaceCalendarService.createHolidayCalendar(
        slug,
        data,
      );
      runInAction(() => {
        this.holidayCalendars = [calendar, ...this.holidayCalendars];
      });
      track("holiday_calendar_created", { calendar_id: calendar.id, source });
      return calendar;
    } finally {
      runInAction(() => {
        this.activeHolidayCalendarId = null;
      });
    }
  }

  // --- Suggested calendars (M15) ---

  async fetchHolidayCalendarSuggestions(slug: string): Promise<IHolidayCalendarSuggestion[]> {
    this.isLoadingHolidayCalendarSuggestions = true;
    try {
      const suggestions =
        (await this.rootStore.workspaceCalendarService.listHolidayCalendarSuggestions(slug)) ?? [];
      runInAction(() => {
        this.holidayCalendarSuggestions = suggestions;
      });
      return suggestions;
    } finally {
      runInAction(() => {
        this.isLoadingHolidayCalendarSuggestions = false;
      });
    }
  }

  /**
   * Hide one suggestion, optimistically — the card disappears on click and the
   * write reconciles behind it. On failure the card is put back where it was, so
   * the list never silently disagrees with what the server will serve next load.
   */
  async dismissHolidayCalendarSuggestion(
    slug: string,
    key: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const previous = this.holidayCalendarSuggestions;
    runInAction(() => {
      this.holidayCalendarSuggestions = previous.filter((suggestion) => suggestion.key !== key);
    });
    try {
      await this.rootStore.workspaceCalendarService.dismissHolidayCalendarSuggestion(slug, key);
      track("holiday_calendar_suggestion_dismissed", { source });
    } catch (error) {
      runInAction(() => {
        this.holidayCalendarSuggestions = previous;
      });
      throw error;
    }
  }

  async getHolidayCalendar(slug: string, calendarId: string): Promise<IHolidayCalendar> {
    const calendar = await this.rootStore.workspaceCalendarService.getHolidayCalendar(
      slug,
      calendarId,
    );
    runInAction(() => {
      // Upsert, not map: on a cold load (deep link / reload) the list has never
      // been fetched, so mapping over an empty array silently threw the calendar
      // we just fetched away and the page rendered "could not be found" (#4235).
      const known = this.holidayCalendars.some((item) => item.id === calendarId);
      this.holidayCalendars = known
        ? this.holidayCalendars.map((item) => (item.id === calendarId ? calendar : item))
        : [calendar, ...this.holidayCalendars];
    });
    return calendar;
  }

  async updateHolidayCalendar(
    slug: string,
    calendarId: string,
    data: IHolidayCalendarWrite,
    source: AnalyticsSource = "page",
  ): Promise<IHolidayCalendar> {
    this.activeHolidayCalendarId = calendarId;
    // Read before the write: `fields` is what this save changed.
    const fields = changedFields(
      data,
      this.holidayCalendars.find((item) => item.id === calendarId),
    );
    try {
      const calendar = await this.rootStore.workspaceCalendarService.updateHolidayCalendar(
        slug,
        calendarId,
        data,
      );
      runInAction(() => {
        this.holidayCalendars = this.holidayCalendars.map((item) =>
          item.id === calendarId ? calendar : item,
        );
      });
      track("holiday_calendar_updated", { calendar_id: calendarId, fields, source });
      return calendar;
    } finally {
      runInAction(() => {
        this.activeHolidayCalendarId = null;
      });
    }
  }

  async deleteHolidayCalendar(
    slug: string,
    calendarId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.activeHolidayCalendarId = calendarId;
    try {
      await this.rootStore.workspaceCalendarService.deleteHolidayCalendar(slug, calendarId);
      track("holiday_calendar_deleted", { calendar_id: calendarId, source });
      runInAction(() => {
        this.holidayCalendars = this.holidayCalendars.filter((item) => item.id !== calendarId);
      });
    } finally {
      runInAction(() => {
        this.activeHolidayCalendarId = null;
      });
    }
  }

  async fetchHolidayCalendarPeople(
    slug: string,
    calendarId: string,
  ): Promise<{ results: IHolidayCalendarPerson[]; totalCount: number }> {
    const { results, total_count } =
      await this.rootStore.workspaceCalendarService.getHolidayCalendarPeople(slug, calendarId);
    return { results, totalCount: total_count };
  }

  async fetchHolidayCalendarDates(
    slug: string,
    calendarId: string,
    query: IHolidayCalendarDateQuery = {},
  ): Promise<THolidayCalendarDatesPage> {
    const page = await this.rootStore.workspaceCalendarService.listHolidayCalendarDates(
      slug,
      calendarId,
      query,
    );
    return {
      results: page.results,
      totalCount: page.total_count,
      // The server sends a cursor even on the last page; only a page that says
      // it has results behind it is worth following.
      nextCursor: page.next_page_results ? page.next_cursor : null,
      hasNext: page.next_page_results,
    };
  }

  async createHolidayCalendarDate(
    slug: string,
    calendarId: string,
    data: IHolidayCalendarDateWrite,
    source: AnalyticsSource = "page",
  ): Promise<IHolidayCalendarDate> {
    const created = await this.rootStore.workspaceCalendarService.createHolidayCalendarDate(
      slug,
      calendarId,
      data,
    );
    track("holiday_date_created", { calendar_id: calendarId, date_id: created.id, source });
    // Adding a date is publish-gated content, so the server flips
    // has_unpublished_changes — mirror it so the Publish button lights up
    // without a refetch of the calendar.
    runInAction(() => {
      this.holidayCalendars = this.holidayCalendars.map((item) =>
        item.id === calendarId ? { ...item, has_unpublished_changes: true } : item,
      );
    });
    return created;
  }

  async importHolidayCalendarDates(
    slug: string,
    calendarId: string,
    file: File,
    source: AnalyticsSource = "page",
  ): Promise<IHolidayCalendarDateImportResult> {
    const result = await this.rootStore.workspaceCalendarService.importHolidayCalendarDates(
      slug,
      calendarId,
      file,
    );
    track("holiday_dates_imported", { calendar_id: calendarId, count: result.created, source });
    // Same publish gate as a hand-added date — but only if the import actually
    // wrote a row; a file whose dates were all already there changes nothing.
    if (result.created > 0) {
      runInAction(() => {
        this.holidayCalendars = this.holidayCalendars.map((item) =>
          item.id === calendarId ? { ...item, has_unpublished_changes: true } : item,
        );
      });
    }
    return result;
  }

  async patchHolidayCalendarDate(
    slug: string,
    calendarId: string,
    dateId: string,
    data: IHolidayCalendarDatePatch,
    source: AnalyticsSource = "page",
    // The row as the caller shows it: the dates live in the panel, not this store.
    // Without it, every key the patch sends counts as changed.
    before?: IHolidayCalendarDate,
  ): Promise<IHolidayCalendarDate> {
    const updated = await this.rootStore.workspaceCalendarService.patchHolidayCalendarDate(
      slug,
      calendarId,
      dateId,
      data,
    );
    // An undefined value is never sent, so it can't be a change.
    const sent = Object.fromEntries(
      Object.entries(data).filter(([, value]) => value !== undefined),
    );
    track("holiday_date_updated", {
      calendar_id: calendarId,
      date_id: dateId,
      fields: changedFields(sent, before),
      source,
    });
    return updated;
  }

  async deleteHolidayCalendarDate(
    slug: string,
    calendarId: string,
    dateId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    await this.rootStore.workspaceCalendarService.deleteHolidayCalendarDate(
      slug,
      calendarId,
      dateId,
    );
    track("holiday_date_deleted", { calendar_id: calendarId, date_id: dateId, source });
  }

  // --- The live ICS URL subscription (M9) ---

  /** Connect a feed and pull it once. The calendar carries its subscription, so
   *  patch it in rather than refetching the list. */
  async connectHolidayCalendarFeed(
    slug: string,
    calendarId: string,
    feedUrl: string,
    source: AnalyticsSource = "page",
  ): Promise<number> {
    const { subscription, synced_count } =
      await this.rootStore.workspaceCalendarService.connectHolidayCalendarFeed(
        slug,
        calendarId,
        feedUrl,
      );
    this.applySubscription(calendarId, subscription);
    // The feed URL is never a prop.
    track("holiday_calendar_feed_connected", {
      calendar_id: calendarId,
      count: synced_count,
      source,
    });
    return synced_count;
  }

  async syncHolidayCalendarFeed(
    slug: string,
    calendarId: string,
    source: AnalyticsSource = "page",
  ): Promise<IHolidayCalendarSyncResult> {
    const result = await this.rootStore.workspaceCalendarService.syncHolidayCalendarFeed(
      slug,
      calendarId,
    );
    this.applySubscription(calendarId, result.subscription);
    track("holiday_calendar_feed_synced", { calendar_id: calendarId, source });
    return result;
  }

  /** Disconnect the feed. Its dates stay behind as manual ones (§7.4.4), so the
   *  caller reloads the dates table to pick up their new Source. */
  async disconnectHolidayCalendarFeed(
    slug: string,
    calendarId: string,
    source: AnalyticsSource = "page",
  ): Promise<number> {
    const { kept } = await this.rootStore.workspaceCalendarService.disconnectHolidayCalendarFeed(
      slug,
      calendarId,
    );
    this.applySubscription(calendarId, null);
    track("holiday_calendar_feed_disconnected", { calendar_id: calendarId, count: kept, source });
    return kept;
  }

  private applySubscription(
    calendarId: string,
    subscription: IHolidayCalendar["subscription"],
  ): void {
    runInAction(() => {
      this.holidayCalendars = this.holidayCalendars.map((item) =>
        item.id === calendarId ? { ...item, subscription } : item,
      );
    });
  }

  async publishHolidayCalendar(
    slug: string,
    calendarId: string,
    source: AnalyticsSource = "page",
  ): Promise<IHolidayCalendar> {
    this.activeHolidayCalendarId = calendarId;
    try {
      const calendar = await this.rootStore.workspaceCalendarService.publishHolidayCalendar(
        slug,
        calendarId,
      );
      track("holiday_calendar_published", { calendar_id: calendarId, source });
      runInAction(() => {
        this.holidayCalendars = this.holidayCalendars.map((item) =>
          item.id === calendarId ? calendar : item,
        );
      });
      return calendar;
    } finally {
      runInAction(() => {
        this.activeHolidayCalendarId = null;
      });
    }
  }

  async discardHolidayCalendarDates(
    slug: string,
    calendarId: string,
    source: AnalyticsSource = "page",
  ): Promise<IHolidayCalendar> {
    this.activeHolidayCalendarId = calendarId;
    try {
      const calendar = await this.rootStore.workspaceCalendarService.discardHolidayCalendarDates(
        slug,
        calendarId,
      );
      track("holiday_dates_discarded", { calendar_id: calendarId, source });
      runInAction(() => {
        this.holidayCalendars = this.holidayCalendars.map((item) =>
          item.id === calendarId ? calendar : item,
        );
      });
      return calendar;
    } finally {
      runInAction(() => {
        this.activeHolidayCalendarId = null;
      });
    }
  }

  async disconnectUserSource(sourceId: string, source: AnalyticsSource = "page"): Promise<void> {
    this.activeSourceId = sourceId;
    try {
      await this.rootStore.workspaceCalendarService.disconnectUserSource(sourceId);
      track("calendar_disconnected", { source_id: sourceId, source });
      runInAction(() => {
        this.userSources = this.userSources.filter((source) => source.id !== sourceId);
      });
    } finally {
      runInAction(() => {
        this.activeSourceId = null;
      });
    }
  }

  async syncUserSource(sourceId: string, source: AnalyticsSource = "page"): Promise<void> {
    this.activeSourceId = sourceId;
    try {
      await this.rootStore.workspaceCalendarService.syncUserSource(sourceId);
      track("calendar_synced", { source_id: sourceId, source });
    } finally {
      runInAction(() => {
        this.activeSourceId = null;
      });
    }
  }

  async getOAuthConnectUrl(
    provider: "google" | "outlook",
    returnPath?: string,
  ): Promise<{ authorization_url: string }> {
    return this.rootStore.workspaceCalendarService.getOAuthConnectUrl(provider, returnPath);
  }

  async fetchGoogleConfig(
    slug: string,
    memberOffset?: number,
  ): Promise<IGoogleDomainWideConfig | null> {
    const isInitial = memberOffset === undefined;
    if (isInitial) {
      this.isLoadingGoogleConfig = true;
    } else {
      this.isLoadingGoogleMembers = true;
    }
    try {
      const result = await this.rootStore.workspaceCalendarService.getGoogleDomainWideConfig(slug, {
        member_offset: memberOffset,
      });

      runInAction(() => {
        if ("id" in result) {
          const config = result as IGoogleDomainWideConfig;
          this.googleConfig =
            !isInitial && this.googleConfig
              ? {
                  ...config,
                  members: [...(this.googleConfig.members ?? []), ...(config.members ?? [])],
                }
              : config;
        } else if (isInitial) {
          this.googleConfig = null;
        }
      });

      return this.googleConfig;
    } finally {
      runInAction(() => {
        if (isInitial) {
          this.isLoadingGoogleConfig = false;
        } else {
          this.isLoadingGoogleMembers = false;
        }
      });
    }
  }

  async createGoogleConfig(
    slug: string,
    data: { service_account_key: string; domain: string },
    source: AnalyticsSource = "page",
  ): Promise<IGoogleDomainWideConfig> {
    this.activeGoogleAction = "create";
    try {
      const config = await this.rootStore.workspaceCalendarService.createGoogleDomainWideConfig(
        slug,
        data,
      );
      runInAction(() => {
        this.googleConfig = config;
      });
      // Before the refetch, so a failed reload doesn't drop it.
      track("calendar_connected", { provider: "google", scope: "workspace", source });
      await this.fetchUserSources();
      return config;
    } finally {
      runInAction(() => {
        this.activeGoogleAction = "";
      });
    }
  }

  async updateGoogleConfig(
    slug: string,
    data: Partial<{ is_enabled: boolean; domain: string }>,
    source: AnalyticsSource = "page",
  ): Promise<IGoogleDomainWideConfig> {
    this.activeGoogleAction = "update";
    // Read before the write: `fields` is what this save changed.
    const fields = changedFields(data, this.googleConfig ?? undefined);
    try {
      const config = await this.rootStore.workspaceCalendarService.updateGoogleDomainWideConfig(
        slug,
        data,
      );
      runInAction(() => {
        this.googleConfig = config;
      });
      track("calendar_config_updated", { provider: "google", fields, source });
      if (data.is_enabled !== undefined) {
        await this.fetchUserSources();
      }
      return config;
    } finally {
      runInAction(() => {
        this.activeGoogleAction = "";
      });
    }
  }

  async deleteGoogleConfig(slug: string, source: AnalyticsSource = "page"): Promise<void> {
    this.activeGoogleAction = "delete";
    try {
      await this.rootStore.workspaceCalendarService.deleteGoogleDomainWideConfig(slug);
      runInAction(() => {
        this.googleConfig = null;
      });
      track("calendar_config_deleted", { provider: "google", source });
      await this.fetchUserSources();
    } finally {
      runInAction(() => {
        this.activeGoogleAction = "";
      });
    }
  }

  async testGoogleConfig(
    slug: string,
    testEmail: string,
    source: AnalyticsSource = "page",
  ): Promise<{ success: boolean; message: string }> {
    this.activeGoogleAction = "test";
    try {
      const result = await this.rootStore.workspaceCalendarService.testGoogleDomainWideConfig(
        slug,
        {
          test_email: testEmail,
        },
      );
      // The test address is never a prop, only whether it worked.
      track("calendar_config_tested", { provider: "google", success: result.success, source });
      return result;
    } finally {
      runInAction(() => {
        this.activeGoogleAction = "";
      });
    }
  }

  async syncGoogleConfig(slug: string, source: AnalyticsSource = "page"): Promise<void> {
    this.activeGoogleAction = "sync";
    try {
      await this.rootStore.workspaceCalendarService.syncGoogleDomainWideConfig(slug);
      track("calendar_config_synced", { provider: "google", source });
    } finally {
      runInAction(() => {
        this.activeGoogleAction = "";
      });
    }
  }

  async fetchOutlookConfig(
    slug: string,
    memberOffset?: number,
  ): Promise<IOutlookDomainWideConfig | null> {
    const isInitial = memberOffset === undefined;
    if (isInitial) {
      this.isLoadingOutlookConfig = true;
    } else {
      this.isLoadingOutlookMembers = true;
    }
    try {
      const result = await this.rootStore.workspaceCalendarService.getOutlookDomainWideConfig(
        slug,
        {
          member_offset: memberOffset,
        },
      );

      runInAction(() => {
        if ("id" in result) {
          const config = result as IOutlookDomainWideConfig;
          this.outlookConfig =
            !isInitial && this.outlookConfig
              ? {
                  ...config,
                  members: [...(this.outlookConfig.members ?? []), ...(config.members ?? [])],
                }
              : config;
        } else if (isInitial) {
          this.outlookConfig = null;
        }
      });

      return this.outlookConfig;
    } finally {
      runInAction(() => {
        if (isInitial) {
          this.isLoadingOutlookConfig = false;
        } else {
          this.isLoadingOutlookMembers = false;
        }
      });
    }
  }

  async createOutlookConfig(
    slug: string,
    data: { tenant_id: string; client_id: string; client_secret: string; domain: string },
    source: AnalyticsSource = "page",
  ): Promise<IOutlookDomainWideConfig> {
    this.activeOutlookAction = "create";
    try {
      const config = await this.rootStore.workspaceCalendarService.createOutlookDomainWideConfig(
        slug,
        data,
      );
      runInAction(() => {
        this.outlookConfig = config;
      });
      // Before the refetch, so a failed reload doesn't drop it.
      track("calendar_connected", { provider: "outlook", scope: "workspace", source });
      await this.fetchUserSources();
      return config;
    } finally {
      runInAction(() => {
        this.activeOutlookAction = "";
      });
    }
  }

  async updateOutlookConfig(
    slug: string,
    data: Partial<{ is_enabled: boolean; domain: string }>,
    source: AnalyticsSource = "page",
  ): Promise<IOutlookDomainWideConfig> {
    this.activeOutlookAction = "update";
    // Read before the write: `fields` is what this save changed.
    const fields = changedFields(data, this.outlookConfig ?? undefined);
    try {
      const config = await this.rootStore.workspaceCalendarService.updateOutlookDomainWideConfig(
        slug,
        data,
      );
      runInAction(() => {
        this.outlookConfig = config;
      });
      track("calendar_config_updated", { provider: "outlook", fields, source });
      if (data.is_enabled !== undefined) {
        await this.fetchUserSources();
      }
      return config;
    } finally {
      runInAction(() => {
        this.activeOutlookAction = "";
      });
    }
  }

  async deleteOutlookConfig(slug: string, source: AnalyticsSource = "page"): Promise<void> {
    this.activeOutlookAction = "delete";
    try {
      await this.rootStore.workspaceCalendarService.deleteOutlookDomainWideConfig(slug);
      runInAction(() => {
        this.outlookConfig = null;
      });
      track("calendar_config_deleted", { provider: "outlook", source });
      await this.fetchUserSources();
    } finally {
      runInAction(() => {
        this.activeOutlookAction = "";
      });
    }
  }

  async testOutlookConfig(
    slug: string,
    testEmail: string,
    source: AnalyticsSource = "page",
  ): Promise<{ success: boolean; message: string }> {
    this.activeOutlookAction = "test";
    try {
      const result = await this.rootStore.workspaceCalendarService.testOutlookDomainWideConfig(
        slug,
        {
          test_email: testEmail,
        },
      );
      // The test address is never a prop, only whether it worked.
      track("calendar_config_tested", { provider: "outlook", success: result.success, source });
      return result;
    } finally {
      runInAction(() => {
        this.activeOutlookAction = "";
      });
    }
  }

  async syncOutlookConfig(slug: string, source: AnalyticsSource = "page"): Promise<void> {
    this.activeOutlookAction = "sync";
    try {
      await this.rootStore.workspaceCalendarService.syncOutlookDomainWideConfig(slug);
      track("calendar_config_synced", { provider: "outlook", source });
    } finally {
      runInAction(() => {
        this.activeOutlookAction = "";
      });
    }
  }
}
